export type Status =
  | 'invited'
  | 'active'
  | 'on_leave_medical'
  | 'on_leave_maternity'
  | 'on_leave_sabbatical'
  | 'on_leave_other'
  | 'inactive'
  | 'substitute'
  | 'temporary'
  | 'terminated';

export type Position =
  | 'Teacher'
  | "Teacher's Aide"
  | 'Specialist'
  | 'Substitute'
  | 'Temporary'
  | 'Leadership'
  | 'Other';

export type Division = 'Boys' | 'Girls' | 'Preschool';
export type EmploymentType = 'Full-time' | 'Part-time';
export type NoteCategory = 'General' | 'Performance' | 'HR' | 'Other';
export type FacultyCommunicationStatus = 'queued' | 'processing' | 'sent' | 'failed';

export interface FacultyAssetDescriptor {
  resourceId: string;
  fileName: string;
  contentType?: string;
  sizeBytes?: number;
  storageKey?: string;
  uploadedAt?: string | number;
}

export interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
  alternatePhone?: string;
}

export interface ScheduleEntry {
  period: number;
  subject: string;
  grade: string;
  room?: string;
  type: 'teaching' | 'prep' | 'duty';
}

export interface TeachingAssignmentClass {
  grade: string;
  subject: string;
  studentCount: number;
  sectionId?: string;
  courseId?: string;
}

export interface TeachingAssignment {
  divisions: Division[];
  grades: string[];
  subjects: string[];
  homeroom?: string;
  schedule: Record<string, ScheduleEntry[]>;
  nextWeekSchedule?: Record<string, ScheduleEntry[]>;
  classes: TeachingAssignmentClass[];
}

export interface FacultyNote {
  id: string;
  content: string;
  author: string;
  timestamp: string;
  category: NoteCategory;
  isImportant: boolean;
  attachments?: FacultyAssetDescriptor[];
}

export interface FacultyCommunication {
  id: string;
  sender: string;
  subject: string;
  date: string;
  preview: string;
  fullContent: string;
  recipients: string[];
  attachments?: FacultyAssetDescriptor[];
  status?: FacultyCommunicationStatus;
  outboxId?: string;
  providerMessageId?: string;
  lastError?: string | null;
}

export type FacultyProfileDraft = Partial<FacultyMember> & {
  profilePhotoFile?: File | null;
  profilePhotoRemoved?: boolean;
};

export interface FacultyNoteDraft {
  content: string;
  category: NoteCategory;
  isImportant: boolean;
  attachmentFile?: File | null;
}

export interface FacultyCommunicationDraft {
  to: string;
  subject: string;
  body: string;
  attachmentFile?: File | null;
}

export interface FacultyMember {
  id: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  hebrewFirstName?: string;
  hebrewLastName?: string;
  preferredName?: string;
  dateOfBirth?: string;
  gender?: 'Male' | 'Female';
  profilePhoto?: string;
  profilePhotoResourceId?: string;
  personalEmail?: string;
  workEmail: string;
  cellPhone: string;
  homePhone?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  employeeId: string;
  hireDate: string;
  position: Position;
  employmentType: EmploymentType;
  status: Status;
  divisions: Division[];
  reportsTo?: string;
  tags: string[];
  emergencyContact?: EmergencyContact;
  teachingAssignment?: TeachingAssignment;
  notes: FacultyNote[];
  communications: FacultyCommunication[];
}

export const FACULTY_STATUS_LABELS: Record<Status, string> = {
  invited: 'Invited / New Hire',
  active: 'Active',
  on_leave_medical: 'On Leave (Medical)',
  on_leave_maternity: 'On Leave (Maternity)',
  on_leave_sabbatical: 'On Leave (Sabbatical)',
  on_leave_other: 'On Leave (Other)',
  inactive: 'Inactive',
  substitute: 'Substitute',
  temporary: 'Temporary',
  terminated: 'Terminated',
};

export const formatFacultyStatus = (status: Status): string =>
  FACULTY_STATUS_LABELS[status] ?? status;
